// Smooth overlays rendered into an image source so they drape over the 3D terrain: sea state (with the exposure
// atlas applied, so it varies bay by bay), wind speed, rain, water temperature. Ink-wash tints on paper.
export class FieldImage {
  constructor(map, id, sampler, colour, opts = {}) {
    this.map = map; this.id = id; this.sampler = sampler; this.colour = colour; this.opacity = opts.opacity ?? 0.6;
    this.field = null; this.step = 0; this.enabled = false; this.W = opts.w || 260; this.H = opts.h || 200;
    this.canvas = document.createElement('canvas'); this.canvas.width = this.W; this.canvas.height = this.H;
    this.beforeId = opts.beforeId; this.addedToMap = false; this.bounds = opts.bounds || [-65.0, 18.2, -64.15, 18.8];
  }
  coords() { const [w, s, e, n] = this.bounds; return [[w, n], [e, n], [e, s], [w, s]]; }
  render() {
    if (!this.field) return null;
    const ctx = this.canvas.getContext('2d'); const img = ctx.createImageData(this.W, this.H); const d = img.data;
    const [w, s, e, n] = this.bounds;
    for (let y = 0; y < this.H; y++) {
      const lat = n - (y + 0.5) / this.H * (n - s);
      for (let x = 0; x < this.W; x++) {
        const lon = w + (x + 0.5) / this.W * (e - w);
        const v = this.sampler(this.field, lon, lat, this.step);
        const [r, g, b, a] = v == null ? [0, 0, 0, 0] : this.colour(v);
        const i = (y * this.W + x) * 4; d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = a;
      }
    }
    ctx.putImageData(img, 0, 0);
    return this.canvas.toDataURL('image/png');
  }
  ensure() {
    if (this.addedToMap || !this.field) return;
    const url = this.render(); if (!url) return;
    this.map.addSource(this.id, { type: 'image', url, coordinates: this.coords() });
    this.map.addLayer({ id: this.id, type: 'raster', source: this.id, paint: { 'raster-opacity': this.enabled ? this.opacity : 0, 'raster-fade-duration': 300, 'raster-resampling': 'linear' }, layout: { visibility: this.enabled ? 'visible' : 'none' } }, this.beforeId);
    this.addedToMap = true;
  }
  update() {
    if (!this.addedToMap) { this.ensure(); return; }
    if (!this.enabled) return;
    const url = this.render(); const src = this.map.getSource(this.id); if (src && url) src.updateImage({ url, coordinates: this.coords() });
  }
  setField(f) { this.field = f; this.update(); }
  setStep(s) { this.step = s; this.update(); }
  setEnabled(on) {
    this.enabled = on; this.ensure(); if (!this.addedToMap) return;
    this.map.setLayoutProperty(this.id, 'visibility', on ? 'visible' : 'none');
    this.map.setPaintProperty(this.id, 'raster-opacity', on ? this.opacity : 0);
    if (on) this.update();
  }
}

const lerp = (a, b, t) => a + (b - a) * t;
const ramp = (stops) => (v) => {
  if (v <= stops[0][0]) return stops[0][1];
  for (let i = 1; i < stops.length; i++) if (v <= stops[i][0]) { const t = (v - stops[i - 1][0]) / (stops[i][0] - stops[i - 1][0]); return stops[i - 1][1].map((c, k) => Math.round(lerp(c, stops[i][1][k], t))); }
  return stops[stops.length - 1][1];
};
// ink washes — muted, paper-friendly
export const RAMPS = {
  sea:  ramp([[0.2, [120, 170, 190, 0]], [0.6, [110, 165, 185, 70]], [1.0, [96, 140, 170, 120]], [1.6, [150, 110, 90, 160]], [2.5, [165, 60, 50, 200]]]),
  wind: ramp([[4, [120, 160, 180, 0]], [10, [120, 150, 170, 60]], [16, [120, 130, 140, 110]], [22, [160, 100, 60, 150]], [30, [170, 55, 45, 190]]]),
  rain: ramp([[10, [90, 120, 170, 0]], [35, [90, 120, 170, 80]], [60, [70, 95, 160, 140]], [90, [60, 60, 140, 190]]]),
  sst:  ramp([[26, [80, 130, 190, 130]], [28, [110, 170, 180, 120]], [29.5, [200, 170, 90, 130]], [31, [190, 90, 60, 160]]]),
};
